import React, { memo, useCallback, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Route, Routes } from 'react-router-dom';
import classNames from 'classnames';
import { getFeatureFlags } from 'utils/feature-flags';

import { ComponentPreloader } from 'components/ComponentPreloader';
import {
  LoadableFavouritesPage,
  LoadableMyOrdersPage,
  LoadableServiceSelection,
  LoadableTrolleyPage,
} from 'components/App/loadableComponents';
import HeaderLogo from 'components/HeaderLogo';
import { useScrolled } from 'hooks/use-scrolled';
import SearchForm from 'components/Search/SearchForm';

// TODO: [SSR][WARN] Does this impact SSR?
// TODO: migrate to useClientSideMediaQuery or SSRMediaQuery
import { useMediaQuery } from 'react-responsive';
import { SSRMediaQuery } from 'components/SSRMediaQuery';
import { BREAKPOINTS } from 'constants/grid';
import { rawUrls } from 'constants/urls';
import { stickyHeaderOffset } from 'constants/site-header';
import MultiSearchStrip from 'components/MultiSearch/Strip';
import { DeliveryPassStatusProvider } from 'components/BookSlot/contexts/DeliveryPassStatus/DeliveryPassStatusProvider';
import { useMinSpend } from 'hooks/use-min-spend';

import { useHeaderHeight } from 'hooks/use-header-height';
import KingWarrant from 'components/Warrant/King';
import { useExperiments } from 'components/Experiment/useExperiments';
import { EOS_NEW_HEADER } from 'constants/monetateExperiments';
import ShoppingSummary from './ShoppingSummary';
import SlotButton from './SlotButton';
import LinksBar from './LinksBar';
import ConnectedLinksBar from './LinksBar/ConnectedLinksBar';
import DropDownNav from './LinksBar/DropDownNav/index';
import DropDownNavHidden from './LinksBar/DropDownNavHidden';
import TopNav from './TopNav';
import MobileOverlay from './Mobile/MobileOverlay';
import SlideOutNav from './SlideOutNav';

import styles from './SiteHeader.scss';
import AuthenticationLinks from './AuthenticationLinks';
import { NewMobileHeader } from './NewMobile';
import { MobileHeader } from './Mobile';

// Ordered according to interaction count * page load speed for each component,
// plus grouped by whether immediately available for navigation, or behind the
// slide out menu.
export const nonDesktopPageLoadPreloadComponents = [
  // Site header linked pages
  LoadableTrolleyPage,
  LoadableServiceSelection,
  // Slide out nav linked pages
  LoadableMyOrdersPage,
  LoadableFavouritesPage,
];

// the static render is responsible for creating non-interactive version of the site for SSR to pre-render
// for customers to look at whilst we client render the page. Once SSR for auth users is in place, it can be
// removed.
const SiteHeader = memo(
  ({
    isLoggedIn = false,
    isHomepage = false,
    staticRender = false,
    clearValue = false,
    isOverlayOpen = false,
    setClearValue = null,
    setMobileOverlay = null,
    showNewHeader,
  }) => {
    const { renderStaticNavLinks } = getFeatureFlags();
    const [isTablet, setIsTablet] = useState(false);

    const { scrolled } = useScrolled(stickyHeaderOffset);

    const toggleOverlay = () => {
      setClearValue(true);
      setMobileOverlay(!isOverlayOpen);
    };

    const mobileHeader = showNewHeader ? (
      <NewMobileHeader isLoggedIn={isLoggedIn} />
    ) : (
      <MobileHeader isLoggedIn={isLoggedIn} toggleOverlay={toggleOverlay} />
    );

    const { showMinSpend } = useMinSpend();

    const headerRef = useRef();
    const multiSearchRef = useRef();
    useHeaderHeight(headerRef);

    const placeholderStyle = useCallback(
      ref => {
        const { current: el } = ref;

        if (!scrolled || !el) return { display: 'none' };

        return { height: el.getBoundingClientRect().height };
      },
      [scrolled],
    );

    const isXLBreakpoint = useMediaQuery({ minWidth: BREAKPOINTS.xl });
    const isTabletBreakpoint = useMediaQuery({
      minWidth: BREAKPOINTS.md,
      maxWidth: BREAKPOINTS.lg,
    });
    useEffect(() => {
      setIsTablet(isTabletBreakpoint);
    }, [isTabletBreakpoint]);
    const placeholder = isTablet ? 'Search...' : 'Search groceries...';

    // Menu is rendered at a different position depending on whether in the
    // scrolled bar or not so that the tabbing order is correct
    const menu = (
      <div className={styles.menu} key="desktopmenu">
        {staticRender ? null : <DropDownNav scrolled={scrolled} />}
      </div>
    );

    const tabletHeader = (
      <SSRMediaQuery minWidth={BREAKPOINTS.md} maxWidth={BREAKPOINTS.lg}>
        <ComponentPreloader components={nonDesktopPageLoadPreloadComponents} />
        <div className={classNames('container-fluid', styles.container)}>
          <div className={classNames(styles.tabletMenu, { [styles.hasMinSpend]: showMinSpend })}>
            <SlideOutNav />
          </div>
          <div className={classNames(styles.tabletLogo, { [styles.hasMinSpend]: showMinSpend })}>
            <HeaderLogo small />
          </div>
          <div
            className={classNames(styles.inner, {
              [styles.isScrolled]: scrolled,
              [styles.hasMinSpend]: showMinSpend,
            })}
          >
            <div className={styles.search}>
              <SearchForm placeholder={placeholder} className={styles.tabletSearchForm} />
            </div>
            <div className={styles.userLinks}>
              <AuthenticationLinks breakpoint="tablet" />
              <div className={styles.slot}>
                <SlotButton />
              </div>
              {isLoggedIn && (
                <div className={styles.trolley}>
                  <ShoppingSummary />
                </div>
              )}
            </div>
          </div>
        </div>
      </SSRMediaQuery>
    );

    const desktopHeader = (
      <SSRMediaQuery minWidth={BREAKPOINTS.lg}>
        <div className={classNames('container-fluid', styles.container)}>
          <section
            className={classNames(styles.branding, {
              [styles.homepageBranding]: isHomepage,
              [styles.scrolled]: scrolled,
            })}
          >
            <div className={styles.logo}>
              <HeaderLogo newHomepage={isHomepage} />
            </div>
            {isHomepage && (
              <div className={styles.warrants}>
                <KingWarrant textWithLeftAlignment />
              </div>
            )}
          </section>
          {scrolled && menu}
          <div
            className={classNames(styles.inner, {
              [styles.isScrolled]: scrolled,
              [styles.hasMinSpend]: showMinSpend,
              [styles.isHomepage]: isHomepage,
            })}
          >
            <div className={styles.search}>
              <SearchForm
                placeholder={placeholder}
                className={classNames(styles.desktopSearchForm, {
                  [styles.withWarrant]: isHomepage,
                })}
              />
            </div>
            <div className={styles.userLinks}>
              <AuthenticationLinks breakpoint={isXLBreakpoint ? 'xlDesktop' : 'desktop'} />
              <div className={styles.slot}>
                <SlotButton />
              </div>
              <div className={styles.trolley}>
                <ShoppingSummary />
              </div>
            </div>
          </div>
          {!scrolled && menu}
          {scrolled ? null : (
            <div className={styles.links}>
              <ConnectedLinksBar isLoggedIn={isLoggedIn} />
            </div>
          )}
        </div>
      </SSRMediaQuery>
    );

    const connectedHeaderComponents = (
      <DeliveryPassStatusProvider>
        <div className={styles.desktop}>
          {renderStaticNavLinks && <DropDownNavHidden />}
          {scrolled && (
            <div data-testid="DesktopHeaderPlaceholder" style={placeholderStyle(headerRef)} />
          )}
          <div
            className={classNames({
              [styles.newDestkopHeader]: showNewHeader,
              [styles.desktopHeader]: desktopHeader,
              [styles.scrolled]: scrolled,
              [styles.hasMinSpend]: showMinSpend,
            })}
            data-testid="DesktopHeader"
            ref={headerRef}
          >
            {mobileHeader}
            {tabletHeader}
            {desktopHeader}
          </div>
        </div>
      </DeliveryPassStatusProvider>
    );

    const staticHeaderComponents = (
      <div className="container-fluid">
        <div className={styles.logo}>
          <HeaderLogo staticRender />
        </div>
        <div className={styles.sticky}>
          <div className={styles.space} />
        </div>

        <div className={styles.links}>
          <LinksBar />
        </div>
      </div>
    );

    return (
      <header className={`${styles.header} no-print`} data-testid="site-header">
        <TopNav staticRender={staticRender} />
        {staticRender ? staticHeaderComponents : connectedHeaderComponents}
        {scrolled && (
          <div
            data-testid="DesktopMultiSearchPlaceholder"
            style={placeholderStyle(multiSearchRef)}
          />
        )}
        {staticRender ? null : (
          <div
            className={classNames(styles.multiSearch, {
              [styles.multiSearchScrolled]: scrolled,
              [styles.hasMinSpend]: showMinSpend,
            })}
            ref={multiSearchRef}
            data-testid="MultiSearchWrapper"
          >
            <Routes>
              <Route path={rawUrls.multiSearch} element={<MultiSearchStrip showNewHeader />} />
            </Routes>
          </div>
        )}
        {isOverlayOpen && <MobileOverlay clearValue={clearValue} setClearValue={setClearValue} />}
      </header>
    );
  },
);

SiteHeader.displayName = 'SiteHeader';

SiteHeader.propTypes = {
  isLoggedIn: PropTypes.bool,
  isOverlayOpen: PropTypes.bool,
  staticRender: PropTypes.bool,
  clearValue: PropTypes.bool,
  setClearValue: PropTypes.func,
  showNewHeader: PropTypes.bool,
};

export default SiteHeader;
